import React from "react";

/**
 * Products / Content: featured extras render inside the catalog grid
 * (between browse chrome and cards), not as sibling page sections.
 */
const ShopInlineFeaturedCtx = React.createContext<React.ReactNode>(null);

export function ShopInlineFeaturedProvider({
  children,
  featured,
}: {
  children: React.ReactNode;
  featured: React.ReactNode;
}) {
  return (
    <ShopInlineFeaturedCtx.Provider value={featured}>
      {children}
    </ShopInlineFeaturedCtx.Provider>
  );
}

export function useShopInlineFeatured(): React.ReactNode {
  return React.useContext(ShopInlineFeaturedCtx);
}

/**
 * Content / Products: every top band (Looks or shop merch) must settle before
 * the default catalog grid starts fetching.
 */
type CatalogDeferApi = {
  registerBand: (id: string) => void;
  unregisterBand: (id: string) => void;
  setBandReady: (id: string, ready: boolean) => void;
};

const CatalogDeferCtx = React.createContext<CatalogDeferApi | null>(null);

export function ContentCatalogDeferProvider({
  children,
  onAllLooksReady,
}: {
  children: React.ReactNode;
  /** Fires when every registered top band has reported ready. */
  onAllLooksReady: () => void;
}) {
  const bandsRef = React.useRef({
    registered: new Set<string>(),
    ready: new Set<string>(),
  });
  const onAllLooksReadyRef = React.useRef(onAllLooksReady);
  onAllLooksReadyRef.current = onAllLooksReady;

  const settleIfAllReady = React.useCallback(() => {
    const { registered, ready } = bandsRef.current;
    if (registered.size === 0) return;
    for (const id of registered) {
      if (!ready.has(id)) return;
    }
    onAllLooksReadyRef.current();
  }, []);

  const api = React.useMemo<CatalogDeferApi>(
    () => ({
      registerBand: (id: string) => {
        bandsRef.current.registered.add(id);
        settleIfAllReady();
      },
      unregisterBand: (id: string) => {
        bandsRef.current.registered.delete(id);
        bandsRef.current.ready.delete(id);
        settleIfAllReady();
      },
      setBandReady: (id: string, ready: boolean) => {
        if (ready) bandsRef.current.ready.add(id);
        else bandsRef.current.ready.delete(id);
        settleIfAllReady();
      },
    }),
    [settleIfAllReady],
  );

  return (
    <CatalogDeferCtx.Provider value={api}>{children}</CatalogDeferCtx.Provider>
  );
}

export function useContentCatalogDefer(): CatalogDeferApi | null {
  return React.useContext(CatalogDeferCtx);
}

/**
 * Top band (Featured Outfit / Recent Looks / Featured Categories / Collections):
 * register with the catalog gate and report when this band can paint.
 */
export function useLooksBandCatalogGate(ready: boolean) {
  const api = useContentCatalogDefer();
  const id = React.useId();

  React.useEffect(() => {
    if (!api) return;
    api.registerBand(id);
    return () => api.unregisterBand(id);
  }, [api, id]);

  React.useEffect(() => {
    if (!api) return;
    api.setBandReady(id, ready);
  }, [api, id, ready]);
}

/** Alias for shop merch bands — same gate as Content Looks. */
export const useMerchBandCatalogGate = useLooksBandCatalogGate;
